import { useEffect, useRef, useState } from "react";
import { Loader2, Search, Clock } from "lucide-react";
import { listConversas } from "../../services/instagramApi";

const FILTROS = [
  { id: "todas",         label: "Todas" },
  { id: "nao-lidas",     label: "Não lidas" },
  { id: "janela-aberta", label: "Janela aberta" },
];

const LIMIT = 20;

function useDebounce(valor, delay) {
  const [debounced, setDebounced] = useState(valor);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(valor), delay);
    return () => clearTimeout(timer);
  }, [valor, delay]);
  return debounced;
}

function formatarHora(isoString) {
  if (!isoString) return "";
  const data = new Date(isoString);
  if (Number.isNaN(data.getTime())) return "";
  const agora = new Date();
  const mesmoDia =
    data.getDate() === agora.getDate() &&
    data.getMonth() === agora.getMonth() &&
    data.getFullYear() === agora.getFullYear();
  if (mesmoDia) {
    return data.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  }
  return data.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

function janelaAberta(janelaExpiraEm) {
  if (!janelaExpiraEm) return false;
  return new Date(janelaExpiraEm).getTime() > Date.now();
}

function Avatar({ nome }) {
  const inicial = (nome || "?")[0].toUpperCase();
  const paletas = [
    "bg-brand-800 text-brand-200",
    "bg-blue-800 text-blue-200",
    "bg-teal-800 text-teal-200",
    "bg-rose-800 text-rose-200",
    "bg-amber-800 text-amber-200",
  ];
  const indice = inicial.charCodeAt(0) % paletas.length;
  return (
    <div
      className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 select-none ${paletas[indice]}`}
      aria-hidden="true"
    >
      {inicial}
    </div>
  );
}

// Extrai um snippet legível da última mensagem (heterogêneo: REST flat ou
// objeto aninhado do socket). Sempre devolve string.
function extrairSnippet(conversa) {
  const um = conversa.ultima_mensagem;
  if (um) {
    if (typeof um === "string") return um;
    if (typeof um.snippet === "string") return um.snippet;
    if (typeof um.text === "string") return um.text;
    const c = um.conteudo;
    if (c) {
      if (typeof c === "string") return c;
      if (typeof c.text === "string") return c.text;
      if (c.text && typeof c.text.body === "string") return c.text.body;
      if (Array.isArray(c.attachments) && c.attachments.length) return "[mídia]";
    }
  }
  return "";
}

function ConversaItem({ conversa, selected, onClick }) {
  const nome   = conversa.cliente_nome || conversa.username || "Desconhecido";
  const user   = conversa.username ? `@${conversa.username}` : "";
  const snippet = extrairSnippet(conversa);
  const hora   = formatarHora(conversa.ultima_mensagem?.data ?? conversa.ultimo_inbound_em);
  const unread = conversa.unread_count || 0;
  const aberta = janelaAberta(conversa.janela_expira_em);

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      aria-label={`Conversa com ${nome}${unread > 0 ? `, ${unread} não lida${unread > 1 ? "s" : ""}` : ""}`}
      className={`w-full text-left flex items-start gap-3 px-3 py-3 border-b border-white/[0.06] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500 ${
        selected ? "bg-surface-2" : "hover:bg-surface-1/80"
      }`}
    >
      <Avatar nome={nome} />

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <span className="text-sm font-semibold text-ink-1 truncate leading-tight">{nome}</span>
          <span className="text-xs text-ink-4 flex-shrink-0 tabular-nums">{hora}</span>
        </div>

        {user && user !== nome && (
          <p className="text-xs text-ink-5 truncate mb-0.5">{user}</p>
        )}

        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-ink-3 truncate">
            {snippet || <span className="italic text-ink-5">Sem mensagens</span>}
          </p>
          {unread > 0 && (
            <span
              className="flex-shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-600 text-white text-[10px] font-bold flex items-center justify-center"
              aria-label={`${unread} não lida${unread > 1 ? "s" : ""}`}
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </div>

        <div className="mt-1.5">
          {aberta ? (
            <span className="inline-flex items-center gap-1.5 text-[10px] font-medium text-emerald-300 bg-emerald-950 border border-emerald-800 rounded-full px-2 py-0.5">
              <Clock className="w-2.5 h-2.5" />
              Janela aberta
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-[10px] font-medium text-ink-4 bg-surface-2 border border-white/[0.08] rounded-full px-2 py-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-surface-4" />
              Janela fechada
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

export default function ConversasList({ contaId, onSelect, selectedId, socket }) {
  const [conversas, setConversas]     = useState([]);
  const [cursor, setCursor]           = useState(null);
  const [hasMore, setHasMore]         = useState(false);
  const [loading, setLoading]         = useState(true);
  const [loadingMais, setLoadingMais] = useState(false);
  const [filtro, setFiltro]           = useState("todas");
  const [busca, setBusca]             = useState("");

  const buscaDebounced = useDebounce(busca, 300);
  const selectedIdRef = useRef(selectedId);
  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);
  // Throttle do reset quando chega mensagem de conversa NOVA não cacheada.
  const ultimoResetRef = useRef(0);

  async function carregar({ reset = false, cursorOverride = undefined } = {}) {
    const cursorParam = cursorOverride !== undefined ? cursorOverride : (reset ? null : cursor);

    if (reset) setLoading(true);
    else setLoadingMais(true);

    try {
      const { conversas: novas, cursor: proxCursor, hasMore: temMais } =
        await listConversas(contaId, {
          limit: LIMIT,
          filtro,
          cursor: cursorParam || undefined,
          busca: buscaDebounced,
        });

      if (reset) setConversas(novas);
      else setConversas((prev) => [...prev, ...novas]);

      setCursor(proxCursor);
      setHasMore(temMais ?? (novas.length === LIMIT));
    } catch {
      // erro silencioso — componente exibe estado vazio
    } finally {
      setLoading(false);
      setLoadingMais(false);
    }
  }

  // Recarrega do zero quando contaId, filtro ou busca debounced mudam
  useEffect(() => {
    carregar({ reset: true, cursorOverride: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contaId, filtro, buscaDebounced]);

  // Socket.IO — real-time
  useEffect(() => {
    if (!socket || !contaId) return;

    socket.emit("join-conversas-ig", contaId);

    function handleMensagemNew(payload) {
      if (!payload || payload.conta_id !== contaId) return;

      setConversas((prev) => {
        const idx = prev.findIndex((c) => c.id === payload.conversa_id);

        if (idx === -1) {
          // Conversa nova ainda não na lista — recarrega com throttle de 2s.
          const agora = Date.now();
          if (agora - ultimoResetRef.current >= 2000) {
            ultimoResetRef.current = agora;
            setTimeout(() => carregar({ reset: true, cursorOverride: null }), 0);
          }
          return prev;
        }

        const antiga = prev[idx];
        const direcao = payload.mensagem?.direcao ?? "in";
        const quando =
          payload.mensagem?.data_criacao ??
          payload.mensagem?.recebido_em ??
          new Date().toISOString();

        const atualizada = {
          ...antiga,
          ultimo_inbound_em: quando,
          ultima_mensagem: {
            ...antiga.ultima_mensagem,
            snippet:
              payload.mensagem?.conteudo?.text ??
              payload.mensagem?.conteudo?.text?.body ??
              (payload.mensagem?.conteudo?.attachments?.length ? "[mídia]" : undefined) ??
              antiga.ultima_mensagem?.snippet ??
              "",
            data: quando,
            direcao,
          },
          unread_count:
            selectedIdRef.current === payload.conversa_id
              ? (antiga.unread_count ?? 0)
              : (antiga.unread_count ?? 0) + 1,
          // Reabre janela de 24h se for mensagem inbound
          janela_expira_em:
            direcao === "in"
              ? new Date(Date.now() + 24 * 3600 * 1000).toISOString()
              : antiga.janela_expira_em,
        };

        const restante = prev.filter((_, i) => i !== idx);
        return [atualizada, ...restante];
      });
    }

    // Reconexão cria socket novo (rooms perdidas) — re-joina e reconcilia.
    function handleReconnect() {
      socket.emit("join-conversas-ig", contaId);
      const agora = Date.now();
      if (agora - ultimoResetRef.current >= 2000) {
        ultimoResetRef.current = agora;
        carregar({ reset: true, cursorOverride: null });
      }
    }

    socket.on("connect",      handleReconnect);
    socket.on("mensagem:new", handleMensagemNew);

    return () => {
      socket.off("connect",      handleReconnect);
      socket.off("mensagem:new", handleMensagemNew);
      socket.emit("leave-conversas-ig", contaId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, contaId]);

  return (
    <div className="flex flex-col h-full bg-base">
      {/* Busca */}
      <div className="px-3 pt-3 pb-2 border-b border-white/[0.06]">
        <div className="relative">
          <Search
            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-5 pointer-events-none"
            aria-hidden="true"
          />
          <input
            type="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por nome ou @usuário..."
            aria-label="Buscar conversa"
            className="w-full bg-surface-1 border border-white/[0.06] rounded text-xs text-ink-2 pl-8 pr-3 py-1.5 placeholder:text-ink-5 focus:outline-none focus:border-brand-600 transition-colors"
          />
        </div>
      </div>

      {/* Filtros */}
      <div
        role="group"
        aria-label="Filtrar conversas"
        className="flex items-center gap-1 px-3 py-2 border-b border-white/[0.06]"
      >
        {FILTROS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => setFiltro(id)}
            aria-pressed={filtro === id}
            aria-label={`Filtrar: ${label}`}
            className={`px-2.5 py-1 rounded text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
              filtro === id
                ? "bg-brand-600 text-white"
                : "text-ink-3 hover:text-ink-2 hover:bg-surface-2"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Lista */}
      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-track-zinc-900 scrollbar-thumb-zinc-700">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-ink-5" />
          </div>
        ) : conversas.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <p className="text-sm text-ink-4">
              {buscaDebounced.trim()
                ? "Nenhuma conversa encontrada para essa busca"
                : filtro !== "todas"
                ? "Nenhuma conversa neste filtro"
                : "Nenhuma conversa ainda"}
            </p>
          </div>
        ) : (
          <>
            {conversas.map((c) => (
              <ConversaItem
                key={c.id}
                conversa={c}
                selected={selectedId === c.id}
                onClick={() => onSelect(c.id)}
              />
            ))}

            {hasMore && (
              <div className="px-3 py-3">
                <button
                  type="button"
                  onClick={() => carregar()}
                  disabled={loadingMais}
                  aria-label="Carregar mais conversas"
                  className="w-full text-xs text-ink-3 hover:text-ink-2 bg-surface-1 hover:bg-surface-2 border border-white/[0.06] rounded py-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50"
                >
                  {loadingMais ? (
                    <span className="inline-flex items-center gap-2 justify-center">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Carregando...
                    </span>
                  ) : (
                    "Carregar mais"
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
